/**
 * 行政区划三级选择 + 详细地址（`region` 字段的学员端控件）
 * -----------------------------------------------------------------------------
 * **一个组件，四个控件**：省 / 市 / 区三级**联动**选择 + 一个详细地址文本框。
 *
 * 为什么合成一个而不是拆成四个字段：
 *   1. 三级**必须联动** —— 拆开会自然出现「省改成了广东、市还是北京」的错配，
 *      而错配的地址会直接污染「客服码按区域匹配」（C-10）。
 *   2. 值本来就是**一个整体**（`RegionValue`）—— 拆开存会让「填了一半」的地址进库。
 *
 * 口径：**D-05 裁决（2026-10-16）**：承载形态 = 3 个级联选择 + 1 个详细地址文本，
 * 封装为单一 `FieldType.REGION` 字段。码表与完整性判定在 `@enroll/shared`（三端同一份），
 * 本文件**只负责渲染**，不含任何行政区划知识。
 *
 * ⚠️ **不设区的市**（东莞 / 中山 / 嘉峪关 / 儋州）只有两级 ——
 * `CascadePicker` 在无 `children` 的节点自然止步，确认时只回传两级，这是**合法**的。
 */
import {
  normalizeRegionValue,
  REGION_DETAIL_MAX_LENGTH,
  type RegionValue,
  regionCascaderOptions,
  regionPathText,
} from '@enroll/shared';
import { CascadePicker, type CascadePickerProps, TextArea } from 'antd-mobile';
import { useMemo, useState } from 'react';
import { useDismissBackGuard } from '../../lib/use-dismiss-back-guard';

/** `CascadePicker` 确认回调的取值类型（从公开 props 推导，避免深链内部类型） */
type PickerConfirmValue = Parameters<NonNullable<CascadePickerProps['onConfirm']>>[0];

export interface RegionFieldProps {
  /** 原始值（`unknown` —— 渲染器不假设表单里存的一定是规范形状） */
  value: unknown;
  onChange: (next: RegionValue) => void;
  /**
   * 失焦时冒泡（渲染器据此做字段级即时提示）。
   *
   * 选完三级要**把新值带上来**：`onChange` 之后渲染器的闭包还是旧值，
   * 用它校验会当场报「必填项未填写」（明明刚选完）。
   */
  onBlur: (next?: RegionValue) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  /** 详细地址占位文案 */
  detailPlaceholder?: string;
  /**
   * 详细地址字数上限。默认取 `REGION_DETAIL_MAX_LENGTH`，但调用方应传字段声明的
   * `validation.maxLength` —— 控件上限必须与校验器同源，否则「UI 让填、校验器拦」。
   */
  detailMaxLength?: number;
}

export function RegionField({
  value,
  onChange,
  onBlur,
  disabled = false,
  invalid = false,
  placeholder = '请选择省 / 市 / 区',
  detailPlaceholder = '详细地址（街道、门牌号）',
  detailMaxLength = REGION_DETAIL_MAX_LENGTH,
}: RegionFieldProps) {
  const region = useMemo(() => normalizeRegionValue(value), [value]);
  const options = useMemo(() => regionCascaderOptions(), []);
  const display = regionPathText(region);

  /**
   * 选择器改成**受控**（`visible`），为的是能占一层返回守卫。
   *
   * 原先用 `actions.open` 非受控打开 —— 那种写法拿不到「现在开着没有」，
   * 全面屏左缘右滑或安卓返回键会直接穿到表单页的「退出填写？」上：
   * 用户只是想关掉一个选择器，却收到「要退出填写吗」。
   */
  const [pickerOpen, setPickerOpen] = useState(false);
  useDismissBackGuard(pickerOpen, () => setPickerOpen(false));

  // `CascadePicker` 的取值是「逐级数组」，空级不能占位（否则会被当成已选空串）
  const pickerValue = useMemo(
    () =>
      [region.provinceCode, region.cityCode, region.districtCode].filter(
        (code): code is string => code !== '',
      ),
    [region.provinceCode, region.cityCode, region.districtCode],
  );

  const handleConfirm = (picked: PickerConfirmValue) => {
    const [province, city, district] = picked;
    // 一律过 `normalizeRegionValue`：它会把「父级不成立」的子码丢掉，
    // 因此即使控件回传了脏组合，也不会写进表单值。
    const next = normalizeRegionValue({
      provinceCode: typeof province === 'string' ? province : '',
      cityCode: typeof city === 'string' ? city : '',
      districtCode: typeof district === 'string' ? district : '',
      detail: region.detail,
    });
    onChange(next);
    onBlur(next);
    setPickerOpen(false);
  };

  return (
    <div className="region-field">
      <CascadePicker
        visible={pickerOpen}
        options={options}
        value={pickerValue}
        title="选择所在地区"
        onConfirm={handleConfirm}
        onCancel={() => {
          setPickerOpen(false);
          onBlur();
        }}
      >
        {() => (
          <button
            type="button"
            disabled={disabled}
            onClick={disabled ? undefined : () => setPickerOpen(true)}
            className={`region-trigger-box${invalid ? ' region-trigger-box--invalid' : ''}`}
          >
            <span
              className={
                display
                  ? 'region-trigger-box__text'
                  : 'region-trigger-box__text region-trigger-box__text--empty'
              }
            >
              {display || placeholder}
            </span>
            <span className="region-trigger-box__chev">›</span>
          </button>
        )}
      </CascadePicker>
      <div className="region-detail">
        <TextArea
          value={region.detail}
          placeholder={detailPlaceholder}
          disabled={disabled}
          rows={2}
          maxLength={detailMaxLength}
          showCount
          onChange={(detail) => onChange({ ...region, detail })}
          onBlur={() => onBlur()}
        />
      </div>
    </div>
  );
}
